import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiValue } from 'react-select';
import { FlairMultiSelectDropdown } from '../../../../../atomic/molecules/FlairDropdown/FlairMultiSelectDropdown/FlairMultiSelectDropdown';
import { FilterSelectOption } from '../../../../../atomic/molecules/FlairDropdown/types';
import { useSkillCategoriesQuery } from '../../../__generated__/graphql';

type Props = {
  value: string[];
  onChange: (value: string[]) => void;
  className?: string;
};

export const SkillCategoryFilter: React.FC<React.PropsWithChildren<Props>> = ({
  value,
  onChange,
  className,
}) => {
  const { t } = useTranslation();
  const { data, loading } = useSkillCategoriesQuery();

  const options = useMemo(
    () =>
      (data?.skillCategories ?? [])
        .slice()
        .sort((a, b) => a.localizedName.localeCompare(b.localizedName))
        .map((c) => ({ value: c.Id, label: c.localizedName })),
    [data],
  );

  useEffect(() => {
    if (loading || !data || !value.length) return;
    const valid = value.filter((v) => options.some((o) => o.value === v));
    if (valid.length !== value.length) {
      onChange(valid);
    }
  }, [loading, data, value, options, onChange]);

  const handleChange = useCallback(
    (selected: MultiValue<FilterSelectOption>) => {
      onChange(selected.map((o) => o.value));
    },
    [onChange],
  );

  return (
    <FlairMultiSelectDropdown
      options={options}
      selectedOptions={value}
      onChange={handleChange}
      placeholder={t('skills.category.pickerLabel')}
      className={className}
      menuPlacement="bottom"
      menuPosition="fixed"
    />
  );
};
